import React, { useCallback } from "react";
import { CustomType, customTypeConstants } from "../../../api/CustomTypes/CustomTypes";
import { CustomTypeEntityType } from "../../../api/CustomTypes/CustomTypesBase";
import { genericEntityConstants, GenericEntityConstantsEntities } from "../../../api/GenericConstants";
import { GenerateCustomFieldValues, useEntityExample } from "../../CustomTypeTemplates/CustomTypeTemplateManager";
import { GenericEntityDetailTable } from "../../../Entities/GenericComponents/common/GenericEntityDetailTable";
import { Tab, Tabs } from "react-bootstrap";
import styles from "./CustomTypePreviewRenderer.module.css";
import { LucideIcon } from "../../../common/icon/LucideIcon";
import { GenericBooleanStateProvider, GenericModal } from "../../../common/modals/Modal/GenericModal";
import { CustomTypeEntityTypeOptionToIcon } from "../CustomTypeRenderUtils";
import { CustomTypeLabelRenderer } from "./CustomTypeRenderer";
import { GenericEntityForm } from "../../../Entities/GenericComponents/common/GenericEntityForm";
import { useEntityPermissions } from "../../../common/permissions/useEntityPermissions";
import { showtoast } from "../../../common/overlays/Toasts/showtoast";
import { DetailsPageTopControlsView } from "../../../common/panels/misc/DetailsPageTopControlsView/DetailsPageTopControlsView";

interface CustomTypePreviewButtonProps {
  customType: CustomType;
}

export const CustomTypePreviewButton = ({ customType }: CustomTypePreviewButtonProps) => {
  return (
    <GenericBooleanStateProvider>
      {({ isTrue, setIsTrue }) => (
        <>
          <button className="btn btn-soft-success" onClick={() => setIsTrue(true)}>
            <LucideIcon name="eye" /> Preview
          </button>
          <CustomTypePreview customType={customType} showModal={isTrue} setShowModal={setIsTrue} />
        </>
      )}
    </GenericBooleanStateProvider>
  );
};

interface CustomTypePreviewProps {
  customType: CustomType;
  showModal: boolean;
  setShowModal: (value: React.SetStateAction<boolean>) => void;
}
export const CustomTypePreview = ({ customType, showModal, setShowModal }: CustomTypePreviewProps) => {
  const entityExample = useEntityExample(customType.entityType);

  return (
    <GenericModal
      showModal={showModal}
      setShowModal={setShowModal}
      modalTitle={
        <div className="flex row-nowrap align-center gap-5">
          Preview of {customTypeConstants.entitySingular} <CustomTypeLabelRenderer entity={{ customType }} /> for{" "}
          <CustomTypeEntityTypeOptionToIcon
            entityType={customType.entityType}
            color={"var(--primary)"}
            width={16}
            height={16}
            className=""
          />
          {entityExample[customType.entityType].name}
        </div>
      }
      modalBody={<CustomTypePreviewRenderer customType={customType} />}
    />
  );
};

interface CustomTypePreviewRendererProps {
  customType: CustomType;
  includeDetailPageHeader?: boolean;
}

export const CustomTypePreviewRenderer = ({ customType, includeDetailPageHeader }: CustomTypePreviewRendererProps) => {
  const entityExample = useEntityExample(customType.entityType);
  const entity = entityExample[customType.entityType];
  const customFields = GenerateCustomFieldValues(customType as any);
  const entityTypeId = customTypeEntityTypeToEntityTypeIdSwitch(customType.entityType);
  const entityPreview = {
    ...entity,
    customType: {
      ...customType,
      id: 0,
      name: customType.name,
      isTemplate: true,
    },
    customFields: customFields,
  };
  const { entityConstants, fieldLabels } = genericEntityConstants[entityTypeId];
  const permissions = useEntityPermissions({ entityTypeId });
  const onSubmit = useCallback(
    (values: any): Promise<void> =>
      new Promise((resolve) => {
        showtoast("success", "Form submitted successfully (not really, this is just a preview).");
        resolve();
      }),
    []
  );
  const onCancel = useCallback(() => {}, []);

  return (
    <div className="flex" style={{ width: "100%", height: "100%", overflow: "hidden" }}>
      <Tabs
        defaultActiveKey="detail"
        id="custom-type-preview-tabs"
        className={styles.previewTabs}
        style={{ width: "100%", height: "100%", overflow: "hidden" }}
        bsStyle="pills"
      >
        <Tab eventKey="detail" title="Detail">
          <div className="flex col-nowrap" style={{ width: "100%", height: "fit-content" }}>
            {!!includeDetailPageHeader && (
              <div style={{ marginTop: "10px" }}>
                <DetailsPageTopControlsView
                  entityTypeId={entityTypeId}
                  entity={entityPreview}
                  entityConstants={entityConstants}
                  hideHistoryModal
                />
              </div>
            )}
            <GenericEntityDetailTable
              entityTypeId={entityTypeId}
              entity={entityPreview}
              componentProps={{ showLinks: false }}
            />
          </div>
        </Tab>
        <Tab eventKey="form" title="Form">
          <GenericEntityForm
            entityTypeId={entityTypeId}
            props={{
              fieldLabels: fieldLabels as any,
              initialValues: entityPreview as any,
              permissions: permissions,
              onSubmit: onSubmit,
              onCancel: onCancel,
              title: `Edit ${entityPreview.name || entityConstants.entitySingular}`,
              loading: false,
              event: "EDIT",
              submitButtonLabel: "Save changes",
              typesOverride: {
                0: customType,
              },
            }}
          />
        </Tab>
      </Tabs>
    </div>
  );
};

// Generics

export const useCustomTypeEntityTypeConstantsSwitch = (entityType?: CustomTypeEntityType) => {
  switch (entityType) {
    case "Dataset":
      return genericEntityConstants["datasets"];
    case "InventoryItem":
      return genericEntityConstants["inventoryItems"];
    case "Person":
      return genericEntityConstants["persons"];
    case "Project":
      return genericEntityConstants["projects"];
    case "Sample":
      return genericEntityConstants["samples"];
    default:
      return null;
  }
};

const customTypeEntityTypeToEntityTypeIdSwitch = (entityType: CustomTypeEntityType): GenericEntityConstantsEntities => {
  switch (entityType) {
    case "Dataset":
      return "datasets";
    case "InventoryItem":
      return "inventoryItems";
    case "Person":
      return "persons";
    case "Project":
      return "projects";
    case "Sample":
      return "samples";
    default:
      throw new Error(`Unsupported entity type: ${entityType}`);
  }
};
